import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import logo from '@/assets/logo.png';
import { usePublicSiteSettings } from '@/hooks/usePublicSiteSettings';
import { formatWhatsAppLink } from '@/lib/format';
import { Button } from '@/components/ui/button';

const navLinks = [
  { href: '/', label: 'Início' },
  { href: '/imoveis', label: 'Imóveis' },
  { href: '/#contato', label: 'Contato' },
  { href: '/#contato', label: 'Anuncie Conosco' },
];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { data: settings } = usePublicSiteSettings();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    if (path.startsWith('/#')) return false;
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (href: string) => {
    if (href.startsWith('/#')) {
      const sectionId = href.replace('/#', '');
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  return (
    <header className="w-full border-b border-border/50 bg-white animate-fade-in-down" style={{ animationDuration: '0.4s' }}>
      <div className="container-custom">
        <div className="flex h-16 items-center justify-between md:h-20">
          {/* Logo - Left */}
          <Link to="/" className="flex items-center">
            <img 
              src={logo} 
              alt="GC Imóveis" 
              width={116}
              height={32}
              className="h-8 w-auto md:h-10"
            />
          </Link>

          {/* Desktop Navigation - Right */}
          <nav className="hidden items-center gap-6 md:flex">
            {navLinks.map((link) => (
              link.href.startsWith('/#') ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }}
                  className="relative py-2 text-sm font-medium tracking-wide text-foreground transition-colors hover:text-primary after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left cursor-pointer"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'relative py-2 text-sm font-medium tracking-wide transition-colors',
                    !isActive(link.href) && 'after:content-[""] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left',
                    isActive(link.href)
                      ? 'text-foreground font-semibold'
                      : 'text-foreground hover:text-foreground/70'
                  )}
                >
                  {link.label}
                </Link>
              )
            ))}
            {settings?.whatsapp && (
              <Button
                size="sm"
                className="ml-4 bg-foreground text-background hover:bg-foreground/90"
                asChild
              >
                <a
                  href={formatWhatsAppLink(settings.whatsapp, 'Olá, quero receber oportunidades de imóveis e lançamentos.')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </a>
              </Button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="flex items-center justify-center rounded-md p-2 text-foreground md:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="border-t border-border pb-4 md:hidden animate-fade-in" style={{ animationDuration: '0.2s' }}>
            <nav className="flex flex-col gap-1 pt-4">
              {navLinks.map((link) => (
                link.href.startsWith('/#') ? (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    className="px-4 py-3 text-base font-medium text-muted-foreground transition-colors hover:bg-accent cursor-pointer"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={cn(
                      'px-4 py-3 text-base font-medium transition-colors',
                      isActive(link.href)
                        ? 'bg-accent text-primary'
                        : 'text-muted-foreground hover:bg-accent'
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              {settings?.whatsapp && (
                <a
                  href={formatWhatsAppLink(settings.whatsapp, 'Olá, quero receber oportunidades de imóveis e lançamentos.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-3"
                  onClick={() => setIsOpen(false)}
                >
                  <Button className="w-full bg-foreground text-background hover:bg-foreground/90">
                    WhatsApp
                  </Button>
                </a>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
